import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableSortLabel from '@mui/material/TableSortLabel';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { api } from '../lib/api';
import { PageHelp } from '../components/PageHelp';
import { startsWithUppercase, UPPERCASE_HELPER_TEXT } from '../lib/validation';
import type { Division } from '../lib/types';

type SortColumn = 'name' | 'active';

/** MyHours-ADR-0014/D-72: Stammdaten sortierbar, gleiches Muster wie DashboardPage.tsx's sortDetailEntries. */
function sortDivisions(
  divisions: Division[],
  column: SortColumn,
  direction: 'asc' | 'desc',
): Division[] {
  const sorted = [...divisions].sort((a, b) => {
    if (column === 'name') return a.name.localeCompare(b.name);
    return Number(a.active) - Number(b.active);
  });
  return direction === 'asc' ? sorted : sorted.reverse();
}

export default function AbteilungenPage() {
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [name, setName] = useState('');
  const [abbreviation, setAbbreviation] = useState('');
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [sortColumn, setSortColumn] = useState<SortColumn>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [createOpen, setCreateOpen] = useState(false);

  async function load() {
    const d = await api.get<Division[]>('/admin/divisions');
    setDivisions(d);
    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    setError(undefined);
  }, [name]);

  async function handleCreate() {
    setError(undefined);
    try {
      await api.post('/admin/divisions', { name, abbreviation });
      setName('');
      setAbbreviation('');
      setCreateOpen(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Abteilung konnte nicht angelegt werden');
    }
  }

  /** D-22: der Hauptverein hat keine Budgetrücklage und erscheint dort nicht. */
  async function toggleMainClub(division: Division) {
    await api.put(`/admin/divisions/${division.divisionId}`, { mainClub: !division.mainClub });
    await load();
  }

  async function toggleActive(division: Division) {
    await api.put(`/admin/divisions/${division.divisionId}`, { active: !division.active });
    await load();
  }

  function handleSort(column: SortColumn) {
    if (column === sortColumn) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }

  async function handleDelete(division: Division) {
    setError(undefined);
    if (!window.confirm(`Abteilung "${division.name}" wirklich löschen?`)) return;
    try {
      await api.del(`/admin/divisions/${division.divisionId}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Abteilung konnte nicht gelöscht werden');
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="h4" sx={{ minWidth: 0 }}>
          Abteilungen
        </Typography>
        <PageHelp text="Hier legt der Admin die Abteilungen des Vereins an. Das Kürzel entspricht den ersten beiden Buchstaben der Kostenstellen-Bezeichnung in SevDesk (z.B. FB für FB-IB)." />
      </Stack>

      <Box>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => {
            setError(undefined);
            setCreateOpen(true);
          }}
        >
          Neue Abteilung
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortColumn === 'name'}
                  direction={sortColumn === 'name' ? sortDirection : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Kürzel</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortColumn === 'active'}
                  direction={sortColumn === 'active' ? sortDirection : 'asc'}
                  onClick={() => handleSort('active')}
                >
                  Aktiv
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <Tooltip title="Der Hauptverein ist der Gesamtverein selbst und hat keine Budgetrücklage.">
                  <span>Hauptverein</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortDivisions(divisions, sortColumn, sortDirection).map((d) => (
              <TableRow key={d.divisionId} hover>
                <TableCell>{d.name}</TableCell>
                <TableCell>{d.abbreviation ?? '—'}</TableCell>
                <TableCell>
                  <Switch checked={d.active} onChange={() => void toggleActive(d)} />
                </TableCell>
                <TableCell>
                  <Switch
                    checked={Boolean(d.mainClub)}
                    onChange={() => void toggleMainClub(d)}
                    slotProps={{ input: { 'aria-label': `${d.name} ist der Hauptverein` } }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Abteilung löschen">
                    <IconButton size="small" onClick={() => void handleDelete(d)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {divisions.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary">Noch keine Abteilungen angelegt.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Neue Abteilung</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name der Abteilung"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
              autoFocus
              error={name.trim() !== '' && !startsWithUppercase(name)}
              helperText={
                name.trim() !== '' && !startsWithUppercase(name) ? UPPERCASE_HELPER_TEXT : undefined
              }
            />
            <TextField
              label="Kürzel (optional)"
              value={abbreviation}
              onChange={(e) => setAbbreviation(e.target.value)}
              helperText="Erste zwei Buchstaben der Kostenstellen-Bezeichnung, z.B. FB"
              fullWidth
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            disabled={!name.trim() || !startsWithUppercase(name)}
            onClick={() => void handleCreate()}
          >
            Anlegen
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
